import { inject, Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  AuthService,
  LocationAwareVehicle,
  Vehicle,
  VehicleApiService,
} from '@fc-core';
import { Update } from '@ngrx/entity';
import {
  eventSourceVehicleUpdates,
  getAllVehicleEntities,
  loadCurrentEmployee,
  realtimeStatusUpdate,
} from '../../../store';
import { ConnectionStatus } from '@fc-store/model/realtime.model';
import { environment } from '../../../../environments/environment';
import {
  setCameraStateFromEventSource,
  setClipProgressFromEventSource,
} from '@fc-vehicles/store';
import { EventSourceClipInterface } from '@fc-core/models/event-source-clip-interface';
import {
  LocationInterface,
  LocationUpdate,
} from '../../../locations/models/location-interface';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, withLatestFrom } from 'rxjs/operators';
import { eventSourceLocationUpdate } from '../../../locations/store';
import { VehicleNoteStore } from '@fc-vehicles/vehicle-notes/store/vehicle-note.store';
import { FileCategories } from '@fc-core/files-uploads/models/FileCategories';
import type { FileProcessingJob } from '@fc-core/files-uploads/models/file-processing-job';
import { DriveLiveEvent } from '@fc-core/models/drive-live-event.model';
import { LiveShareLiveEvent } from '@fc-core/models/live-share-live-event.model';

export class EventSourceMessage {
  id: number;
  createdAt: Date;
  vehicle: LocationAwareVehicle;
  hash: string;

  static messagesFromJSON(msgs: any[]): EventSourceMessage[] {
    return msgs.map((msg) => {
      return {
        id: msg.id,
        createdAt: new Date(msg.created),
        hash: msg.hash,
        vehicle: VehicleApiService.fromJSON<LocationAwareVehicle>(msg.vehicle),
      };
    });
  }
}

export class FileUpdate {
  customerId: string;
  id: number;
  objectId: string;
  status: string;
  type: FileCategories;
  jobs: FileProcessingJob[];
}

@Injectable({
  providedIn: 'root',
})
export class EventSourceService implements OnDestroy {
  private globalEventSource: EventSource;
  private cameraEventSource: EventSource;
  private employeeEventSource: EventSource;
  private driveEventSource: EventSource | null = null;
  private liveShareEventSource: EventSource | null = null;
  private readonly vehicleMovementKey = 'movement';
  // Wake-from-sleep / long-hidden handling. EventSource.readyState can lie
  // for the same reason WebSocket.readyState does — after OS suspend the
  // TCP socket is half-open and the browser's auto-reconnect never fires
  // because nothing told it the connection died.
  private hiddenSince: number | null = null;
  private sleepWatchdogTimer: ReturnType<typeof setInterval> | null = null;
  private lastWatchdogTick = Date.now();
  private lastForcedReconnectAt = 0;
  // Whether a consumer (e.g. the fleet map) currently wants vehicle movement
  // events — connectToGlobalEventSource re-attaches the listener on the new
  // EventSource so it survives reconnects.
  private vehicleListenerAttached = false;
  private readonly VISIBILITY_FORCE_RECONNECT_MS = 30000;
  private readonly SLEEP_WATCHDOG_INTERVAL_MS = 15000;
  // On wake, visibilitychange and the sleep watchdog usually both fire;
  // skip a second forced re-dial this close to the first.
  private readonly FORCE_RECONNECT_COOLDOWN_MS = 5000;
  vehicleChanges = new BehaviorSubject<Update<Vehicle>[]>([]);
  private hasGlobalStreamOpened = false;
  private readonly globalReconnectedSubject = new Subject<void>();
  readonly globalReconnected$ = this.globalReconnectedSubject.asObservable();
  private readonly filesUpdateSubject = new Subject<FileUpdate>();
  // A stream, not a signal: file events are discrete and arrive in bursts. A
  // scalar signal coalesces rapid .set()s and the effect scheduler only sees
  // the last one, so all but the final file in a burst would be dropped.
  readonly filesUpdate$ = this.filesUpdateSubject.asObservable();
  private readonly driveUpdateSubject = new Subject<DriveLiveEvent>();
  readonly driveUpdate$ = this.driveUpdateSubject.asObservable();
  private readonly driveReconnectedSubject = new Subject<void>();
  readonly driveReconnected$ = this.driveReconnectedSubject.asObservable();
  private readonly liveShareUpdateSubject = new Subject<LiveShareLiveEvent>();
  readonly liveShareUpdate$ = this.liveShareUpdateSubject.asObservable();
  private readonly liveShareReconnectedSubject = new Subject<void>();
  readonly liveShareReconnected$ =
    this.liveShareReconnectedSubject.asObservable();
  vehicleNoteStore = inject<VehicleNoteStore>(VehicleNoteStore);

  get vehicleChanges$() {
    return this.vehicleChanges.asObservable().pipe(
      withLatestFrom(this.store.select(getAllVehicleEntities)),
      filter(([vehicleChanges, _]) => vehicleChanges.length > 0),
    );
  }

  constructor(
    private store: Store,
    private auth: AuthService,
  ) {
    this.onVehicleUpdate = this.onVehicleUpdate.bind(this);

    // # DO this should inject window HACk
    window.addEventListener('offline', () => {
      if (this.globalEventSource?.readyState === EventSource.OPEN) {
        this.globalEventSource.close();
        this.store.dispatch(
          realtimeStatusUpdate({ status: ConnectionStatus.DISCONNECTED }),
        );
      }
    });

    window.addEventListener('online', () => {
      // Recreate global EventSource when back online
      if (
        !this.globalEventSource ||
        this.globalEventSource.readyState === EventSource.CLOSED
      ) {
        this.connectToGlobalEventSource();
      }
    });

    document.addEventListener('visibilitychange', this.onVisibilityChange);

    this.startSleepWatchdog();
  }

  ngOnDestroy(): void {
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    if (this.sleepWatchdogTimer) {
      clearInterval(this.sleepWatchdogTimer);
      this.sleepWatchdogTimer = null;
    }
  }

  private onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      this.hiddenSince = Date.now();
      return;
    }
    const hiddenFor =
      this.hiddenSince !== null ? Date.now() - this.hiddenSince : 0;
    this.hiddenSince = null;
    if (hiddenFor >= this.VISIBILITY_FORCE_RECONNECT_MS) {
      this.forceReconnectGlobalEventSource();
    }
  };

  // Tear down the global SSE regardless of readyState and re-dial. Mirrors
  // the WebSocket force-reconnect path used by ChatWsService: after a long
  // suspend the readyState can still read OPEN, so the standard reconnect
  // gate would silently skip recovery.
  private forceReconnectGlobalEventSource(): void {
    // "Force *re*connect" only — if nothing has dialed the global SSE yet
    // (e.g. pre-auth), don't initiate a connection nobody asked for.
    if (!this.globalEventSource) return;
    // On wake the network is often still down; let the online handler
    // (or EventSource's own retry) recover instead of a doomed handshake.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    const now = Date.now();
    if (now - this.lastForcedReconnectAt < this.FORCE_RECONNECT_COOLDOWN_MS) {
      return;
    }
    this.lastForcedReconnectAt = now;
    this.globalEventSource.close();
    this.connectToGlobalEventSource();
  }

  private startSleepWatchdog(): void {
    this.lastWatchdogTick = Date.now();
    this.sleepWatchdogTimer = setInterval(() => {
      const now = Date.now();
      const delta = now - this.lastWatchdogTick;
      this.lastWatchdogTick = now;
      // setInterval pauses during OS sleep; a delta much larger than the
      // configured interval means we just woke up.
      if (delta > this.SLEEP_WATCHDOG_INTERVAL_MS * 3) {
        this.forceReconnectGlobalEventSource();
      }
    }, this.SLEEP_WATCHDOG_INTERVAL_MS);
  }

  closeCameraEventSource() {
    this.cameraEventSource?.close();
  }

  connectToCameraEventSource(cameraId: number) {
    this.cameraEventSource = new EventSource(
      `${environment.eventSourceHost}/dvr/${cameraId}/?auth=${this.auth.token}`,
    );
    this.cameraEventSource.addEventListener('cameraState', (event) => {
      const statusList = JSON.parse(event.data);
      this.store.dispatch(setCameraStateFromEventSource({ state: statusList }));
    });
    this.cameraEventSource.addEventListener('progress', (event) => {
      const eventSourceClip: EventSourceClipInterface = JSON.parse(event.data);
      this.store.dispatch(
        setClipProgressFromEventSource({
          clip: {
            id: eventSourceClip.event.id,
            changes: eventSourceClip.event,
          },
        }),
      );
    });
  }

  connectToEmployeeEventSource(employeeId: string): void {
    if (!!this.employeeEventSource) return;
    this.employeeEventSource = new EventSource(
      `${environment.eventSourceHost}/employee/${employeeId}/?auth=${this.auth.token}`,
    );
    this.employeeEventSource.addEventListener('permissions', () =>
      // load new permissions after update from event source
      this.store.dispatch(loadCurrentEmployee()),
    );
  }

  connectToDriveEventSource(): void {
    if (this.driveEventSource) return;
    this.driveEventSource = new EventSource(
      `${environment.eventSourceHost}/drive/?auth=${this.auth.token}`,
    );
    this.driveEventSource.addEventListener('update', (event) => {
      this.driveUpdateSubject.next(JSON.parse(event.data) as DriveLiveEvent);
    });
    let hasDriveStreamOpened = false;
    this.driveEventSource.addEventListener('open', () => {
      if (hasDriveStreamOpened) this.driveReconnectedSubject.next();
      hasDriveStreamOpened = true;
    });
  }

  closeDriveEventSource(): void {
    this.driveEventSource?.close();
    this.driveEventSource = null;
  }

  connectToLiveShareEventSource(): void {
    if (this.liveShareEventSource) return;
    this.liveShareEventSource = new EventSource(
      `${environment.eventSourceHost}/live-share-updates/?auth=${this.auth.token}`,
    );
    this.liveShareEventSource.addEventListener('update', (event) => {
      this.liveShareUpdateSubject.next(
        JSON.parse(event.data) as LiveShareLiveEvent,
      );
    });
    let hasLiveShareStreamOpened = false;
    this.liveShareEventSource.addEventListener('open', () => {
      if (hasLiveShareStreamOpened) this.liveShareReconnectedSubject.next();
      hasLiveShareStreamOpened = true;
    });
  }

  closeLiveShareEventSource(): void {
    this.liveShareEventSource?.close();
    this.liveShareEventSource = null;
  }

  connectToGlobalEventSource(): void {
    this.globalEventSource = new EventSource(
      `${environment.eventSourceHost}/events/?auth=${this.auth.token}`,
    );
    this.globalEventSource.addEventListener('open', () => {
      this.store.dispatch(
        realtimeStatusUpdate({ status: ConnectionStatus.CONNECTED }),
      );
      if (this.hasGlobalStreamOpened) this.globalReconnectedSubject.next();
      this.hasGlobalStreamOpened = true;
    });
    this.globalEventSource.addEventListener('error', (event) => {
      const eventSource = event.target as EventSource;
      if (eventSource.readyState === EventSource.CONNECTING) {
        this.store.dispatch(
          realtimeStatusUpdate({ status: ConnectionStatus.CONNECTING }),
        );
      }
    });

    this.addLocationUpdateListener();
    this.addFilesUpdateListener();
    // Re-attach the vehicle movement listener if a consumer had subscribed —
    // listeners on the previous (closed) EventSource don't carry over.
    if (this.vehicleListenerAttached) {
      this.globalEventSource.addEventListener(
        this.vehicleMovementKey,
        this.onVehicleUpdate,
      );
    }
  }

  private addFilesUpdateListener() {
    this.globalEventSource?.addEventListener('upload_file', (event) => {
      this.filesUpdateSubject.next(JSON.parse(event.data) as FileUpdate);
    });
  }

  private addLocationUpdateListener() {
    this.globalEventSource?.addEventListener(
      'location_update',
      (event: MessageEvent) => {
        const locations: LocationInterface[] = (
          JSON.parse(event.data) as LocationUpdate[]
        ).map((update: LocationUpdate) => update.location);
        this.store.dispatch(eventSourceLocationUpdate({ locations }));
      },
    );
  }

  addVehicleUpdateListener(): void {
    this.vehicleListenerAttached = true;
    this.globalEventSource?.addEventListener(
      this.vehicleMovementKey,
      this.onVehicleUpdate,
    );
  }

  removeVehicleUpdateListener(): void {
    this.vehicleListenerAttached = false;
    this.globalEventSource?.removeEventListener(
      this.vehicleMovementKey,
      this.onVehicleUpdate,
    );
  }

  private onVehicleUpdate(event: MessageEvent): void {
    const updates = EventSourceMessage.messagesFromJSON(JSON.parse(event.data));
    const locationAwareVehicles = updates.map((update): Update<Vehicle> => {
      return {
        id: update.vehicle.id,
        changes: {
          lastReading: update.vehicle.lastReading,
          lastState: update.vehicle.lastState,
          hash: update.hash,
          powerSource: update.vehicle.powerSource,
          status: update.vehicle.status,
        },
      };
    });
    this.vehicleChanges.next(locationAwareVehicles);
    this.store.dispatch(
      eventSourceVehicleUpdates({ vehicles: locationAwareVehicles }),
    );
  }
}
